import { type MaydayBeacon, type DadProfile, getDadDisplayName } from '../types';
import { getCurrentProfile } from '../services/authService';
import { respondToMayday } from '../services/maydayService';
import { acceptCrewRequest, declineCrewRequest, removeFromCrew } from '../services/crewService';
import { showToast } from './Toast';

export function renderMaydayBeaconView(maydays: MaydayBeacon[], allUsers: DadProfile[] = []): string {
  const profile = getCurrentProfile();
  const currentName = getDadDisplayName(profile);
  const currentUid = profile?.uid || '';
  
  const myCrewUids = profile?.crew || [];
  const pendingReceivedUids = profile?.crewRequestsReceived || [];
  
  // Friended dads in your Crew
  const friendedDads = allUsers.filter(c => myCrewUids.includes(c.uid));
  // Incoming requests
  const pendingRequests = allUsers.filter(c => pendingReceivedUids.includes(c.uid));

  return `
    <div class="space-y-6 animate-fade-in font-sans">
      
      <!-- Mayday Broadcast Hero Card -->
      <div class="bg-[#0a0c13] border border-pink-500/50 rounded-2xl p-5 sm:p-6 shadow-[0_0_20px_rgba(255,0,85,0.15)] relative overflow-hidden">
        <div class="laser-line-pink absolute top-0 left-0 right-0"></div>
        <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 relative z-10">
          <div class="space-y-1.5">
            <div class="flex items-center gap-2">
              <span class="px-2.5 py-0.5 rounded text-[11px] font-black bg-transparent text-pink-400 border border-pink-500 uppercase tracking-wider flex items-center gap-1 shadow-[0_0_8px_rgba(255,0,85,0.4)]">
                <i class="fa-solid fa-triangle-exclamation"></i> Mayday Beacon
              </span>
              <span class="text-xs text-slate-300 font-medium">Quick-Assist Peer Network (1–3 Mile Radius)</span>
            </div>
            <h2 class="text-xl font-black text-white">Need a second set of hands right now?</h2>
            <p class="text-xs sm:text-sm text-slate-300 max-w-xl leading-relaxed">
              Moving a cast-iron tub, bleeding brakes, or need a 10mm socket in a pinch? Send an urgent 15-minute neighbor ping to your connected Crew.
            </p>
          </div>
          <button id="broadcast-mayday-btn" class="btn-danger px-5 py-3 rounded-xl text-xs sm:text-sm flex items-center gap-2 flex-shrink-0 cursor-pointer">
            <i class="fa-solid fa-bullhorn"></i> Broadcast Mayday
          </button>
        </div>
      </div>

      <!-- Active Maydays Section -->
      <div>
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-sm font-bold uppercase tracking-wider text-pink-400 flex items-center gap-2" style="text-shadow: 0 0 8px rgba(255, 42, 133, 0.4);">
            <i class="fa-solid fa-satellite-dish"></i> Active Live Pings
          </h3>
          <span class="text-xs text-slate-500">Pings expire automatically after 2 hours</span>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          ${maydays.length === 0 ? `
            <div class="col-span-full p-6 bg-[#0a0c13] border border-white/[0.08] rounded-xl text-xs text-slate-400 text-center space-y-1">
              <i class="fa-solid fa-circle-check text-cyan-400 text-2xl mb-1"></i>
              <div class="font-bold text-slate-200">No active Maydays right now</div>
              <p class="text-slate-500">All neighbor projects are running smoothly in your area!</p>
            </div>
          ` : maydays.map(m => {
            const isHelping = m.helpers.includes(currentName);
            const minutesLeft = Math.max(1, Math.round((m.expiresAt - Date.now()) / (1000 * 60)));

            return `
              <div class="bg-[#0a0c13] border border-pink-500/40 rounded-xl p-4 sm:p-5 space-y-3 shadow-[0_0_12px_rgba(255,0,85,0.12)] relative">
                
                <div class="flex items-center justify-between gap-2">
                  <div class="flex items-center gap-2.5">
                    <button class="view-dad-profile-btn flex items-center gap-2 text-left group cursor-pointer" data-dad-uid="${m.authorId}">
                      <div class="w-9 h-9 rounded-lg bg-transparent text-pink-400 flex items-center justify-center text-xs font-black border border-pink-500/50 shadow-[0_0_8px_rgba(255,0,85,0.3)] group-hover:border-pink-400">
                        ${m.avatarInitials}
                      </div>
                      <div>
                        <span class="font-bold text-slate-200 text-xs block group-hover:text-pink-300 group-hover:underline">${m.authorName}</span>
                        <span class="text-[10px] text-slate-500">${m.area} &bull; Expires in ${minutesLeft}m</span>
                      </div>
                    </button>
                  </div>
                  <span class="px-2.5 py-0.5 rounded text-[10px] font-black bg-transparent text-pink-400 border border-pink-500/60 shadow-[0_0_6px_rgba(255,0,85,0.3)]">
                    ${m.urgency}
                  </span>
                </div>

                <p class="text-xs sm:text-sm text-slate-200 font-medium leading-relaxed bg-transparent p-3 rounded-lg border border-white/[0.08]">
                  "${m.text}"
                </p>

                ${m.reward ? `
                  <div class="text-[11px] text-amber-400 flex items-center gap-1.5 bg-transparent px-2.5 py-1 rounded-md border border-amber-400/40 shadow-[0_0_8px_rgba(255,183,3,0.15)]">
                    <i class="fa-solid fa-gift text-xs"></i> <span>${m.reward}</span>
                  </div>
                ` : ''}

                <div class="flex items-center justify-between pt-2 border-t border-white/[0.08] text-xs">
                  <span class="text-[11px] text-slate-400 flex items-center gap-1">
                    <i class="fa-solid fa-hand-holding-hand text-cyan-400"></i>
                    <strong>${m.helpers.length} dad${m.helpers.length === 1 ? '' : 's'} responding</strong>
                  </span>
                  
                  ${isHelping ? `
                    <span class="px-3 py-1.5 rounded-lg bg-transparent text-emerald-400 border border-emerald-400/60 text-xs font-bold flex items-center gap-1 shadow-[0_0_8px_rgba(0,255,136,0.25)]">
                      <i class="fa-solid fa-check"></i> On the Way!
                    </span>
                  ` : `
                    <button data-mayday-id="${m.id}" class="respond-mayday-btn btn-primary px-3.5 py-1.5 rounded-lg text-xs flex items-center gap-1.5 cursor-pointer">
                      <i class="fa-solid fa-person-running"></i> I Can Help (10m)
                    </button>
                  `}
                </div>

              </div>
            `;
          }).join('')}
        </div>
      </div>

      <!-- Pending Crew Requests Section -->
      ${pendingRequests.length > 0 ? `
        <div class="pt-4 border-t border-white/[0.08]">
          <div class="flex items-center justify-between mb-3">
            <div>
              <h3 class="text-sm font-bold uppercase tracking-wider text-emerald-400 flex items-center gap-2">
                <i class="fa-solid fa-user-clock"></i> Pending Crew Requests (${pendingRequests.length})
              </h3>
              <p class="text-xs text-slate-400">These dads sent you an invitation to join each other's Crew.</p>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            ${pendingRequests.map(reqDad => `
              <div class="p-4 bg-emerald-950/20 border border-emerald-500/40 rounded-xl flex items-center justify-between gap-3 shadow-[0_0_12px_rgba(0,255,136,0.1)]">
                <div class="flex items-center gap-3 min-w-0">
                  <div class="w-10 h-10 rounded-xl bg-transparent text-emerald-400 border border-emerald-500/50 flex items-center justify-center font-bold text-sm flex-shrink-0">
                    ${reqDad.avatarInitials}
                  </div>
                  <div class="min-w-0 text-xs">
                    <button class="view-dad-profile-btn font-bold text-slate-200 hover:text-emerald-300 hover:underline truncate block text-left" data-dad-uid="${reqDad.uid}">
                      ${getDadDisplayName(reqDad)}
                    </button>
                    <p class="text-[11px] text-emerald-400/80 truncate">Friend Request Received</p>
                  </div>
                </div>
                <div class="flex items-center gap-1.5 flex-shrink-0">
                  <button data-accept-uid="${reqDad.uid}" class="crew-accept-btn btn-primary px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 cursor-pointer">
                    <i class="fa-solid fa-check"></i> Accept
                  </button>
                  <button data-decline-uid="${reqDad.uid}" class="crew-decline-btn btn-secondary px-2.5 py-1.5 rounded-lg text-xs font-bold text-slate-400 hover:text-white cursor-pointer">
                    Decline
                  </button>
                </div>
              </div>
            `).join('')}
          </div>
        </div>
      ` : ''}

      <!-- Friended Dads (The Crew List) -->
      <div class="pt-4 border-t border-white/[0.08]">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-base font-bold text-slate-200 flex items-center gap-2">
              <i class="fa-solid fa-users text-cyan-400"></i> The Crew (${friendedDads.length})
            </h3>
            <p class="text-xs text-slate-400">Your personal list of friended dads connected through Pops Culture</p>
          </div>
          <span class="text-xs font-semibold px-2.5 py-1 rounded-full bg-transparent text-cyan-400 border border-cyan-400/40 shadow-[0_0_8px_rgba(0,240,255,0.2)]">
            ${friendedDads.length} Dads in Your Crew
          </span>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          ${friendedDads.length === 0 ? `
            <div class="col-span-full p-8 bg-[#0a0c13] border border-white/[0.08] rounded-xl text-center space-y-3">
              <div class="w-14 h-14 mx-auto rounded-2xl border border-white/10 flex items-center justify-center text-slate-500 text-2xl">
                <i class="fa-solid fa-user-group"></i>
              </div>
              <div class="space-y-1">
                <h4 class="font-bold text-slate-200 text-sm">Your Crew is Empty</h4>
                <p class="text-xs text-slate-400 max-w-md mx-auto leading-relaxed">
                  The Crew is your personal list of friended dads. Whenever you discover a dad in an event or mission, click on their profile to add them to your Crew. Once they accept, you'll both be in each other's Crew list!
                </p>
              </div>
              <div>
                <button id="crew-explore-missions-btn" class="btn-primary px-4 py-2 rounded-xl text-xs font-bold inline-flex items-center gap-2 cursor-pointer shadow-[0_0_12px_rgba(0,240,255,0.25)]">
                  <i class="fa-solid fa-compass"></i> Find Dads in Missions
                </button>
              </div>
            </div>
          ` : friendedDads.map(c => `
            <div class="card-workshop p-4 flex flex-col justify-between gap-3">
              <div class="flex items-start justify-between gap-3">
                <button class="view-dad-profile-btn flex items-center gap-3 text-left group cursor-pointer" data-dad-uid="${c.uid}">
                  <div class="w-11 h-11 rounded-xl bg-transparent text-cyan-400 border border-cyan-400/40 flex items-center justify-center font-bold text-sm shadow-[0_0_8px_rgba(0,240,255,0.2)] group-hover:border-cyan-400 flex-shrink-0">
                    ${c.avatarInitials}
                  </div>
                  <div class="space-y-0.5 text-xs min-w-0">
                    <div class="flex items-center gap-1.5">
                      <h4 class="font-bold text-slate-200 group-hover:text-cyan-300 group-hover:underline truncate">${getDadDisplayName(c)}</h4>
                      <span class="w-2 h-2 rounded-full ${c.status === 'active' ? 'bg-emerald-400 shadow-[0_0_6px_rgba(0,255,136,0.6)]' : 'bg-slate-600'}" title="${c.status === 'active' ? 'Active' : 'Offline'}"></span>
                    </div>
                    <p class="text-[11px] text-cyan-400 font-medium truncate">Crew Member</p>
                  </div>
                </button>
              </div>

              <!-- Action Bar for Friended Dad -->
              <div class="flex items-center justify-between pt-2.5 border-t border-white/[0.08] text-xs">
                <button data-dad-uid="${c.uid}" class="view-dad-profile-btn text-cyan-400 hover:text-cyan-300 text-[11px] font-semibold flex items-center gap-1 cursor-pointer">
                  <i class="fa-solid fa-id-card"></i> View Resume
                </button>

                <div class="flex items-center gap-2">
                  <button data-crew-name="${getDadDisplayName(c)}" class="crew-chat-btn text-slate-400 hover:text-cyan-400 p-1.5 text-xs transition-colors cursor-pointer" title="Message Dad">
                    <i class="fa-solid fa-comment-dots text-base"></i>
                  </button>
                  <button data-remove-uid="${c.uid}" data-remove-name="${getDadDisplayName(c)}" class="crew-remove-btn text-slate-500 hover:text-pink-400 p-1.5 text-xs transition-colors cursor-pointer" title="Remove from Crew">
                    <i class="fa-solid fa-user-minus"></i>
                  </button>
                </div>
              </div>
            </div>
          `).join('')}
        </div>
      </div>

    </div>
  `;
}

export function bindMaydayBeaconEvents(
  onOpenBroadcastModal: () => void,
  onViewDadProfile?: (dadUid: string) => void,
  onTabSwitch?: (tab: 'feed') => void,
  onRefresh?: () => void
): void {
  // Broadcast Mayday
  document.getElementById('broadcast-mayday-btn')?.addEventListener('click', () => {
    onOpenBroadcastModal();
  });

  // Respond to Mayday
  document.querySelectorAll('.respond-mayday-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const maydayId = (btn as HTMLElement).dataset.maydayId;
      const profile = getCurrentProfile();
      if (maydayId && profile) {
        await respondToMayday(maydayId, getDadDisplayName(profile));
        showToast(`You answered the call! Coordinate in neighborhood chat.`, 'success');
      }
    });
  });

  // Message Dad
  document.querySelectorAll('.crew-chat-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const name = (btn as HTMLElement).dataset.crewName;
      showToast(`Private neighborhood channel opened with ${name}.`, 'info');
    });
  });

  // View Dad Profile
  document.querySelectorAll('.view-dad-profile-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const dadUid = (btn as HTMLElement).dataset.dadUid;
      if (dadUid && onViewDadProfile) {
        onViewDadProfile(dadUid);
      }
    });
  });

  // Accept incoming friend request
  document.querySelectorAll('.crew-accept-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const uid = (btn as HTMLElement).dataset.acceptUid;
      if (uid) {
        try {
          await acceptCrewRequest(uid);
          showToast(`Dad added to your Crew!`, 'success');
          if (onRefresh) onRefresh();
        } catch (err: any) {
          showToast(err.message || 'Error accepting request', 'error');
        }
      }
    });
  });

  // Decline incoming friend request
  document.querySelectorAll('.crew-decline-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const uid = (btn as HTMLElement).dataset.declineUid;
      if (uid) {
        try {
          await declineCrewRequest(uid);
          showToast(`Request declined.`, 'info');
          if (onRefresh) onRefresh();
        } catch (err: any) {
          showToast(err.message || 'Error declining request', 'error');
        }
      }
    });
  });

  // Remove from Crew
  document.querySelectorAll('.crew-remove-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const uid = (btn as HTMLElement).dataset.removeUid;
      const name = (btn as HTMLElement).dataset.removeName || 'this dad';
      if (uid && confirm(`Remove ${name} from your Crew?`)) {
        try {
          await removeFromCrew(uid);
          showToast(`${name} removed from your Crew.`, 'info');
          if (onRefresh) onRefresh();
        } catch (err: any) {
          showToast(err.message || 'Error removing from crew', 'error');
        }
      }
    });
  });

  // Explore missions empty state button
  document.getElementById('crew-explore-missions-btn')?.addEventListener('click', () => {
    if (onTabSwitch) onTabSwitch('feed');
  });
}
